/**
 * Client mirror of server/src/utils/trackingSlots.js.
 * Two Xero tracking slots. Missing `tracking` is read as the old single category.
 */

export const IDENTITY_FIELDS = new Set(['Name', 'Number', 'Both']);

export function emptySlot(slot) {
  return {
    slot,
    enabled: false,
    jtField: '',
    xeroCategoryID: '',
    xeroCategoryName: ''
  };
}

function sanitizeSlot(raw, slot) {
  const source = raw && typeof raw === 'object' ? raw : {};
  return {
    slot,
    enabled: source.enabled === true,
    jtField: source.jtField != null ? String(source.jtField) : '',
    xeroCategoryID: source.xeroCategoryID != null ? String(source.xeroCategoryID) : '',
    xeroCategoryName: source.xeroCategoryName != null ? String(source.xeroCategoryName) : ''
  };
}

function legacySlot(settings, fieldKey) {
  const source = settings && typeof settings === 'object' ? settings : {};
  return {
    slot: 1,
    enabled: source.enabled === true,
    jtField: source[fieldKey] != null ? String(source[fieldKey]) : '',
    xeroCategoryID: source.xeroCategoryID != null ? String(source.xeroCategoryID) : '',
    xeroCategoryName: source.xeroCategoryName != null ? String(source.xeroCategoryName) : ''
  };
}

function slotHasConfig(slot) {
  return !!(slot && (slot.enabled || slot.jtField || slot.xeroCategoryID || slot.xeroCategoryName));
}

export function slotsFromSettings(settings, fieldKey) {
  const raw = Array.isArray(settings?.tracking) ? settings.tracking : [];
  if (!raw.length) {
    const slot = legacySlot(settings, fieldKey);
    return slotHasConfig(slot) ? [slot] : [];
  }
  return raw.slice(0, 2).map((entry, index) => sanitizeSlot(entry, Number(entry?.slot) || index + 1));
}

/** Always two slots, for the settings form. */
export function uiSlotsFromSettings(settings, fieldKey) {
  const stored = slotsFromSettings(settings, fieldKey);
  return [1, 2].map((slot) => stored.find((entry) => entry.slot === slot) || emptySlot(slot));
}

export function legacyFromSlot1(slots, fieldKey) {
  const slot1 = (slots || []).find((entry) => entry.slot === 1) || (slots || [])[0] || emptySlot(1);
  return {
    [fieldKey]: slot1.jtField || '',
    xeroCategoryName: slot1.xeroCategoryName || '',
    xeroCategoryID: slot1.xeroCategoryID || '',
    enabled: slot1.enabled === true
  };
}

export function validateSlots(slots) {
  const enabled = (slots || []).filter((slot) => slot && slot.enabled);
  const categories = enabled.map((slot) => slot.xeroCategoryID || slot.xeroCategoryName).filter(Boolean);
  if (new Set(categories).size !== categories.length) {
    return 'The two tracking categories must be different Xero categories.';
  }
  const fields = enabled.map((slot) => slot.jtField).filter(Boolean);
  if (new Set(fields).size !== fields.length) {
    return 'The two tracking categories must use different JobTread fields.';
  }
  for (const slot of enabled) {
    if (!slot.jtField || !(slot.xeroCategoryID || slot.xeroCategoryName)) {
      return `Tracking category ${slot.slot} needs a JobTread field and a Xero category.`;
    }
  }
  return null;
}

export function slotReady(slot) {
  return !!(slot?.jtField && (slot.xeroCategoryID || slot.xeroCategoryName));
}

/** The category whose option is the job name, number, or both. */
export function jobIdentitySlot(settings, fieldKey) {
  return slotsFromSettings(settings, fieldKey)
    .find((slot) => slot.enabled && IDENTITY_FIELDS.has(slot.jtField) && slot.xeroCategoryName) || null;
}
